/**
 * Work Log Review raw-event log — task 159c §D7.
 *
 * Continuous list of events (one row per event) with sticky group
 * headers when a group-by is active. Eight columns from locked
 * decision #6: time, employee, app, title, url/site, project,
 * classification source, billable.
 *
 * Pagination model: the 159a route returns one server page at a time
 * (default pageSize=100, max 200). The component shows Prev/Next +
 * a page-size select. Per the 159c plan, this is the agreed
 * "virtualized in spirit": only the visible page lives in the DOM.
 */

import { useMemo } from 'react';
import { Badge } from '@/components/primitives/Badge';
import { Button } from '@/components/primitives/Button';
import { Select } from '@/components/primitives/Select';
import { dayOfWeekInTz, formatDayShort, WEEKDAY_NAMES_LONG } from '@/lib/dates';
import type {
  ClassificationSource,
  GroupByDimension,
  WorkLogReviewEventRow,
} from '@/types/workLogReview';
import styles from './WorkLogReviewEventLog.module.css';

interface WorkLogReviewEventLogProps {
  events: readonly WorkLogReviewEventRow[];
  groupBy: readonly GroupByDimension[];
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /** Event ids currently selected for the bulk Classify action. */
  selectedEventIds: ReadonlySet<string>;
  /** Called when the user toggles a per-row checkbox. */
  onToggleSelect: (eventId: string) => void;
  /** Called when the user toggles the select-all checkbox in the header. */
  onToggleSelectAll: (selectAll: boolean, eventIds: readonly string[]) => void;
  /** Opens the Classify modal scoped to a single event. */
  onClassifyEvent: (eventId: string) => void;
}

const PAGE_SIZE_OPTIONS = [
  { value: '50', label: '50 per page' },
  { value: '100', label: '100 per page' },
  { value: '200', label: '200 per page' },
] as const;

const SOURCE_BADGE: Record<ClassificationSource, 'general' | 'enabled' | 'admin' | 'owner'> = {
  fallback: 'general',
  rule: 'enabled',
  manual: 'admin',
  ai: 'owner',
};

// Row badge wording matches the filter checkbox in WorkLogReviewFilters —
// the engineer term "fallback" never reaches the admin user.
const SOURCE_LABEL: Record<ClassificationSource, string> = {
  fallback: 'Default',
  rule: 'Rule',
  manual: 'Manual',
  ai: 'AI',
};

function formatTime(iso: string): string {
  // ISO shape is fixed: YYYY-MM-DDTHH:MM:SS(.sss)Z. We render the day
  // through formatDayShort and slice the wall-clock UTC time off the
  // string directly — avoids `new Date()` so the eslint guard against
  // raw Date construction stays clean (the construction-allow-list is
  // src/data/, src/lib/dates.ts, and a couple of Date-aware composites).
  const day = formatDayShort(iso);
  const hh = iso.slice(11, 13);
  const mm = iso.slice(14, 16);
  return `${day}, ${hh}:${mm}`;
}

function eventGroupKey(e: WorkLogReviewEventRow, groupBy: readonly GroupByDimension[]): string {
  if (groupBy.length === 0) return '';
  return groupBy
    .map((dim) => {
      switch (dim) {
        case 'app':
          return e.app ?? '(none)';
        case 'appFileName':
          return e.appFileName ?? '(none)';
        case 'title':
          return e.title ?? '(none)';
        case 'rootDomain':
          return e.rootDomain ?? '(none)';
        case 'site':
          return e.site ?? '(none)';
        case 'url':
          return e.url ?? '(none)';
        case 'employee':
          return e.employeeName ?? '(none)';
        case 'businessUnit':
          return '(none)';
        case 'project':
          return e.project?.name ?? '(none)';
        case 'client':
          return e.project?.clientName ?? '(none)';
        case 'classificationSource':
          return e.classificationSource ?? '(none)';
        case 'classifiedByRule':
          return e.classifiedByRuleId ?? '(none)';
        case 'day':
          return e.occurredAt.slice(0, 10);
        case 'hour':
          return e.occurredAt.slice(0, 13) + ':00';
        case 'weekday':
          return WEEKDAY_NAMES_LONG[dayOfWeekInTz(e.occurredAt.slice(0, 10), 'UTC')];
      }
    })
    .join(' › ');
}

export function WorkLogReviewEventLog({
  events,
  groupBy,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  selectedEventIds,
  onToggleSelect,
  onToggleSelectAll,
  onClassifyEvent,
}: WorkLogReviewEventLogProps) {
  // When grouped, walk the events in occurredAt order and emit a sticky
  // header row each time the group key changes. The events are already
  // server-sorted by occurredAt DESC.
  const grouped: Array<{ header: string | null; rows: WorkLogReviewEventRow[] }> = useMemo(() => {
    if (groupBy.length === 0) return [{ header: null, rows: [...events] }];
    const out: Array<{ header: string | null; rows: WorkLogReviewEventRow[] }> = [];
    let current: { header: string; rows: WorkLogReviewEventRow[] } | null = null;
    for (const e of events) {
      const key = eventGroupKey(e, groupBy);
      if (!current || current.header !== key) {
        current = { header: key, rows: [] };
        out.push(current);
      }
      current.rows.push(e);
    }
    return out;
  }, [events, groupBy]);

  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1;
  const lastIndex = Math.min(totalCount, page * pageSize);
  const firstIndex = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;

  const pageEventIds = useMemo(() => events.map((e) => e.id), [events]);
  const allOnPageSelected =
    pageEventIds.length > 0 && pageEventIds.every((id) => selectedEventIds.has(id));
  const someOnPageSelected =
    !allOnPageSelected && pageEventIds.some((id) => selectedEventIds.has(id));

  return (
    <section className={styles.wrapper} aria-labelledby="wlr-eventlog-heading">
      <header className={styles.header}>
        <h2 id="wlr-eventlog-heading" className={styles.heading}>
          Raw events
        </h2>
        <span className={styles.range}>
          {totalCount === 0
            ? 'No events in the current filter window.'
            : `Showing ${firstIndex}–${lastIndex} of ${totalCount.toLocaleString()}`}
        </span>
      </header>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <caption className={styles.srOnly}>Raw event log for Work Log Review.</caption>
          <thead>
            <tr>
              <th scope="col" className={styles.selectCol}>
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someOnPageSelected;
                  }}
                  onChange={(e) => onToggleSelectAll(e.target.checked, pageEventIds)}
                  aria-label="Select all events on this page"
                  disabled={pageEventIds.length === 0}
                  data-testid="wlr-select-all"
                />
              </th>
              <th scope="col">Time</th>
              <th scope="col">Employee</th>
              <th scope="col">App</th>
              <th scope="col">Title</th>
              <th scope="col">Site / URL</th>
              <th scope="col">Project</th>
              <th scope="col">Source</th>
              <th scope="col">Billable</th>
              <th scope="col" className={styles.actionsCol}>
                <span className={styles.srOnly}>Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {totalCount === 0 ? (
              <tr>
                <td colSpan={10} className={styles.empty}>
                  No events match these filters.
                </td>
              </tr>
            ) : (
              grouped.map((seg, segIdx) => (
                <RenderSegment
                  key={segIdx}
                  header={seg.header}
                  rows={seg.rows}
                  selectedEventIds={selectedEventIds}
                  onToggleSelect={onToggleSelect}
                  onClassifyEvent={onClassifyEvent}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <footer className={styles.pager}>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          data-testid="wlr-pager-prev"
        >
          Previous
        </Button>
        <span className={styles.pageOf}>
          Page {page} of {totalPages}
        </span>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          data-testid="wlr-pager-next"
        >
          Next
        </Button>
        <Select
          value={String(pageSize)}
          options={PAGE_SIZE_OPTIONS}
          onChange={(v) => onPageSizeChange(Number(v))}
          label="Page size"
          size="sm"
        />
      </footer>
    </section>
  );
}

interface SegmentProps {
  header: string | null;
  rows: WorkLogReviewEventRow[];
  selectedEventIds: ReadonlySet<string>;
  onToggleSelect: (eventId: string) => void;
  onClassifyEvent: (eventId: string) => void;
}

function RenderSegment({
  header,
  rows,
  selectedEventIds,
  onToggleSelect,
  onClassifyEvent,
}: SegmentProps) {
  return (
    <>
      {header !== null && (
        <tr className={styles.groupHeaderRow}>
          <th colSpan={10} scope="rowgroup" className={styles.groupHeader}>
            <span className={styles.groupHeaderLabel}>{header}</span>
            <span className={styles.groupHeaderCount}>{rows.length.toLocaleString()} events</span>
          </th>
        </tr>
      )}
      {rows.map((e) => (
        <tr key={e.id} className={styles.row}>
          <td className={styles.selectCell}>
            <input
              type="checkbox"
              checked={selectedEventIds.has(e.id)}
              onChange={() => onToggleSelect(e.id)}
              aria-label={`Select event ${e.title ?? e.id}`}
              data-testid="wlr-select-event"
            />
          </td>
          <td className={styles.timeCell}>{formatTime(e.occurredAt)}</td>
          <td>{e.employeeName ?? '(unknown)'}</td>
          <td>{e.app ?? '(none)'}</td>
          <td className={styles.titleCell} title={e.title ?? ''}>
            {e.title ?? '(none)'}
          </td>
          <td className={styles.urlCell} title={e.url ?? e.site ?? ''}>
            {e.site ?? e.url ?? '(none)'}
          </td>
          <td>{e.project ? `${e.project.clientName} › ${e.project.name}` : '(unset)'}</td>
          <td>
            {e.classificationSource ? (
              <Badge variant={SOURCE_BADGE[e.classificationSource]}>
                {SOURCE_LABEL[e.classificationSource]}
              </Badge>
            ) : (
              '(none)'
            )}
          </td>
          <td className={styles.billableCell}>
            {e.billable === null ? '—' : e.billable ? 'Yes' : 'No'}
          </td>
          <td className={styles.actionsCell}>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onClassifyEvent(e.id)}
              data-testid="wlr-classify-event"
            >
              Classify
            </Button>
          </td>
        </tr>
      ))}
    </>
  );
}
